import { declaredType, uploadProblem, type UploadSlot } from '@eklevo/shared/uploads'
import { errorCode, errorReason } from './errors'

/*
 * Uploads from the browser (ADR 0009 §6), for the course editor (the console's and the back
 * office's) and the company wizard's logo: the file goes straight to the imports bucket in parts
 * of 10 MiB (one for a small file), with the addresses the server signs ten at a time, and the page
 * then asks whether the malware scan has answered, with a growing pause, saying «Analizando»
 * meanwhile. Each surface plugs in its API (UploadApi): the console's `hr.uploads`, the back
 * office's `uploads`. Nothing of the file is kept by the page.
 */

/** A signed PUT of a part: its address, which fixes its size. */
export interface SignedPut {
  url: string
}

/** The steps of an upload on a surface's API. */
export interface UploadApi {
  start: (input: { slot: UploadSlot; size: number; contentType: string }) => Promise<{
    token: string
    parts: { count: number; size: number }
  }>
  parts: (input: { token: string; numbers: number[] }) => Promise<{
    parts: (SignedPut & { number: number })[]
  }>
  finish: (input: { token: string; parts: { number: number; etag: string }[] }) => Promise<unknown>
  check: (input: {
    token: string
  }) => Promise<{ status: 'scanning' | 'clean' } | { status: 'refused'; reason: string }>
  abort: (input: { token: string }) => Promise<unknown>
  /** The PUT and the pause between checks, when not the browser's own (the tests'). */
  put?: PutFn
  wait?: (ms: number, signal: AbortSignal) => Promise<void>
}

/**
 * Why an upload did not finish: a refusal of the server (UPLOAD_REFUSALS: too large, of another
 * type, past the month's allowance, a threat…), or the page's own: `cancelled`, `network` (a PUT
 * that did not arrive), `scan-timeout` (no answer of the scan in 15 minutes) or `failed`.
 */
export class UploadFailure extends Error {
  readonly reason: string
  constructor(reason: string) {
    super(reason)
    this.reason = reason
  }
}

export type UploadProgress =
  { phase: 'sending'; sent: number; total: number } | { phase: 'scanning' }

/** A PUT of the browser: the bytes sent so far as it goes, and the ETag the store answers. */
export type PutFn = (
  put: SignedPut,
  body: Blob,
  opts: { signal: AbortSignal; onSent: (bytes: number) => void },
) => Promise<string | null>

/**
 * The PUT with XMLHttpRequest, the only browser request that says how much it has sent. The
 * imports bucket answers each part's ETag (its CORS lets the page read it). A part goes without a
 * type: the server gave the upload its type when it started it. An upload cancelled before is not
 * sent at all (an abort event never fires again for a signal already aborted).
 */
export const xhrPut: PutFn = (put, body, { signal, onSent }) =>
  new Promise((resolve, reject) => {
    if (signal.aborted) return reject(new UploadFailure('cancelled'))
    const xhr = new XMLHttpRequest()
    const stop = () => xhr.abort()
    const settle = (outcome: () => void) => () => {
      signal.removeEventListener('abort', stop)
      outcome()
    }
    xhr.open('PUT', put.url)
    xhr.upload.onprogress = (e) => onSent(e.loaded)
    xhr.onload = settle(() =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve(xhr.getResponseHeader('ETag'))
        : reject(new UploadFailure('network')),
    )
    xhr.onerror = settle(() => reject(new UploadFailure('network')))
    xhr.onabort = settle(() => reject(new UploadFailure('cancelled')))
    signal.addEventListener('abort', stop, { once: true })
    xhr.send(body)
  })

/** Waits, unless the upload is cancelled meanwhile or was before. */
export const pause = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal.aborted) return reject(new UploadFailure('cancelled'))
    const stop = () => {
      clearTimeout(timer)
      reject(new UploadFailure('cancelled'))
    }
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', stop)
      resolve()
    }, ms)
    signal.addEventListener('abort', stop, { once: true })
  })

/** The pauses between checks of the scan: a second, then longer, then every 5 seconds. */
export const SCAN_PAUSES = [1000, 2000, 3000, 5000] as const
/** How long the page waits for the scan before it gives up. */
export const SCAN_WAIT_MS = 15 * 60_000

/** A failed call of the API as an upload's failure: its reason when the server gave one. */
function failure(err: unknown): UploadFailure {
  if (err instanceof UploadFailure) return err
  const reason = errorReason(err)
  if (errorCode(err) === 'NOT_FOUND') return new UploadFailure('expired')
  return new UploadFailure(reason && reason !== 'invalid-input' ? reason : 'failed')
}

export interface SendOptions {
  signal: AbortSignal
  onProgress: (progress: UploadProgress) => void
  /** The PUT (XMLHttpRequest by default); the tests give their own. */
  put?: PutFn
  /** The pause between checks (setTimeout by default). */
  wait?: (ms: number, signal: AbortSignal) => Promise<void>
}

/**
 * Sends a file to its place and waits until the scan has found it clean: its upload's token. What
 * the page can tell before sending (too large, empty, not of the place's type) is refused before
 * anything is asked. An upload cancelled or failed while sending is given up on the server too.
 * Cancelled at any moment, even while a call to the server is on its way, it stops as that call
 * answers (`cancelled`): never a token for an upload the page gave up.
 */
export async function sendUpload(
  api: UploadApi,
  slot: UploadSlot,
  file: File,
  opts: SendOptions,
): Promise<string> {
  const { signal, onProgress } = opts
  const stopped = () => {
    if (signal.aborted) throw new UploadFailure('cancelled')
  }
  const put = opts.put ?? api.put ?? xhrPut
  const wait = opts.wait ?? api.wait ?? pause
  const problem = uploadProblem(slot, file)
  if (problem) throw new UploadFailure(problem)
  const started = await api
    .start({ slot, size: file.size, contentType: declaredType(slot, file) })
    .catch((err: unknown) => {
      throw failure(err)
    })
  const { token } = started
  const give = async (err: unknown): Promise<never> => {
    await api.abort({ token }).catch(() => undefined)
    // Whatever the PUT said as it was stopped: cancelled.
    throw signal.aborted ? new UploadFailure('cancelled') : failure(err)
  }
  try {
    stopped()
    onProgress({ phase: 'sending', sent: 0, total: file.size })
    const { count, size } = started.parts
    const sent: { number: number; etag: string }[] = []
    for (let first = 1; first <= count; first += 10) {
      const numbers = Array.from({ length: Math.min(10, count - first + 1) }, (_, i) => first + i)
      const signed = await api.parts({ token, numbers })
      stopped()
      for (const part of signed.parts) {
        const start = (part.number - 1) * size
        const etag = await put(part, file.slice(start, start + size), {
          signal,
          onSent: (bytes) =>
            onProgress({ phase: 'sending', sent: start + bytes, total: file.size }),
        })
        stopped()
        if (!etag) throw new UploadFailure('network')
        sent.push({ number: part.number, etag })
      }
    }
    await api.finish({ token, parts: sent })
    stopped()
  } catch (err) {
    return give(err)
  }
  onProgress({ phase: 'scanning' })
  const until = Date.now() + SCAN_WAIT_MS
  for (let i = 0; ; i++) {
    const checked = await api.check({ token }).catch((err: unknown) => {
      throw signal.aborted ? new UploadFailure('cancelled') : failure(err)
    })
    stopped()
    if (checked.status === 'clean') return token
    if (checked.status === 'refused') throw new UploadFailure(checked.reason)
    if (Date.now() > until) throw new UploadFailure('scan-timeout')
    await wait(SCAN_PAUSES[Math.min(i, SCAN_PAUSES.length - 1)]!, signal)
  }
}

/** Where an upload stands, as its field shows it; none once done (or cancelled). */
export type UploadState =
  | { status: 'sending'; fileName: string; percent: number }
  | { status: 'scanning'; fileName: string }
  | { status: 'failed'; fileName: string; reason: string }

/**
 * The uploads a page has in flight, each by the place it goes to (a lesson's video, a material…),
 * so that they go on while the page shows something else, and their fields can show them (with
 * useSyncExternalStore). An upload that finishes leaves nothing; one that fails, its reason, until
 * it is cleared or the place gets another file.
 */
export class UploadTracker {
  private states = new Map<string, UploadState>()
  private controllers = new Map<string, AbortController>()
  private listeners = new Set<() => void>()
  private snapshot: ReadonlyMap<string, UploadState> = new Map()

  subscribe = (listener: () => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  getSnapshot = () => this.snapshot

  private set(key: string, state: UploadState | null): void {
    if (state) this.states.set(key, state)
    else this.states.delete(key)
    this.snapshot = new Map(this.states)
    for (const listener of this.listeners) listener()
  }

  /** Whether some upload is still being sent or scanned: leaving now would lose it. */
  busy(): boolean {
    return [...this.states.values()].some((s) => s.status !== 'failed')
  }

  /**
   * Uploads `file` to the place `key`: `run` sends it (sendUpload) and does what the page does
   * once it is clean (records it, puts its id in the document).
   */
  start(
    key: string,
    file: File,
    run: (opts: Pick<SendOptions, 'signal' | 'onProgress'>) => Promise<void>,
  ): void {
    this.controllers.get(key)?.abort()
    const controller = new AbortController()
    this.controllers.set(key, controller)
    const fileName = file.name
    this.set(key, { status: 'sending', fileName, percent: 0 })
    const mine = () => this.controllers.get(key) === controller
    run({
      signal: controller.signal,
      onProgress: (p) => {
        if (!mine()) return
        this.set(
          key,
          p.phase === 'scanning'
            ? { status: 'scanning', fileName }
            : {
                status: 'sending',
                fileName,
                percent: p.total ? Math.floor((100 * p.sent) / p.total) : 0,
              },
        )
      },
    }).then(
      () => {
        if (!mine()) return
        this.controllers.delete(key)
        this.set(key, null)
      },
      (err: unknown) => {
        if (!mine()) return
        this.controllers.delete(key)
        const reason = err instanceof UploadFailure ? err.reason : failure(err).reason
        this.set(key, reason === 'cancelled' ? null : { status: 'failed', fileName, reason })
      },
    )
  }

  /** A file the page refuses before sending it (too large, not of the place's type): why. */
  refuse(key: string, fileName: string, reason: string): void {
    this.controllers.get(key)?.abort()
    this.controllers.delete(key)
    this.set(key, { status: 'failed', fileName, reason })
  }

  /**
   * «Cancelar»: the upload stops (its parts on the server go) and leaves nothing, at once: what
   * its run does once the call on its way answers is no longer the place's.
   */
  cancel(key: string): void {
    const controller = this.controllers.get(key)
    if (!controller) return
    controller.abort()
    this.controllers.delete(key)
    this.set(key, null)
  }

  /** Forgets a failed upload's reason. */
  clear(key: string): void {
    if (this.states.get(key)?.status === 'failed') this.set(key, null)
  }

  /** Stops every upload (the page goes away). */
  stopAll(): void {
    for (const controller of this.controllers.values()) controller.abort()
  }
}
